import { useQuery } from 'react-query';
import { authFetch } from '../../../../CommonUtils/authFetch';
import { urlWithParams } from '../../../../CommonUtils/urlWithParams';
import { whatIfsQueryKeys } from './queryKeys';
import { IncomeModel } from '../../../../ProfileOPL/Income/api/useIncomeQuery';
import { PensionPlanType } from '../components/WhatIf/ProfileChanges/PensionChange/PensionChange.meta';

export interface EarmarkValueDTO {
  EarmarkType: number;
  Dependent: {
    DependentType: number;
    Id: string;
    IsDependent: boolean;
    PersonalInfo: {
      ContactInformation: string | null;
      Dob: string;
      FirstName: string;
      LastName: string;
      YearOfBirth: number | null;
    };
  } | null;
  Name: string | null;
}

export interface BaseWhatIfDTO {
  WhatIfKey: string;
  WhatIfType: number;
  WhatIfTypeName: string;
}

export interface BaseSalesWhatIfDTO<SaleDTO> extends BaseWhatIfDTO {
  Sales: SaleDTO[];
}

export interface BusinessSaleDTO {
  AssetId: string;
  SaleYear: string;
  NetProceedsFromSale: {
    YearsAfterSaleYear: number;
    SaleAmount: number;
    NetWorthPercent: number;
  }[];
  Earmarks: EarmarkValueDTO[];
}

export interface RealEstateSaleDTO {
  AssetId: string;
  SaleYear: string;
  SaleValue: number;
  Earmarks: EarmarkValueDTO[];
}

export interface MiscellaneousAssetSaleDTO {
  AssetId: string;
  SaleYear: string;
  SaleValue: number;
  Earmarks: EarmarkValueDTO[];
}

export interface IncomeChangeDTO extends BaseWhatIfDTO {
  Incomes: IncomeModel[];
}

export interface PensionChangeDTO extends BaseWhatIfDTO {
  Pensions: {
    AssetId: string;
    Name: string;
    IsPrincipal: boolean;
    PlanType: PensionPlanType;
    MonthlyPayment: number;
    DistributionAtRetirement: boolean;
    DistributionAge: number | null;
    IsInflationAdjusted: boolean;
    InflationRate: number | null;
    SurvivorBenefit: number;
    LumpSumAmount: number;
  }[];
}

export type WhatIfDTO =
  | BaseSalesWhatIfDTO<BusinessSaleDTO>
  | BaseSalesWhatIfDTO<RealEstateSaleDTO>
  | BaseSalesWhatIfDTO<MiscellaneousAssetSaleDTO>
  | IncomeChangeDTO
  | PensionChangeDTO;

export interface WhatIfTypeDTO {
  DefaultTitle: string;
  IsApplied: boolean;
  Title: string;
  WhatIf: WhatIfDTO;
}

interface FetchWhatIfGroupsParams {
  clientId: number;
  fpId: string;
  whatIfAreaId: string;
  goalId?: string;
}

const fetchWhatIfGroups = ({
  clientId,
  fpId,
  whatIfAreaId,
  goalId,
}: FetchWhatIfGroupsParams): Promise<Record<string, WhatIfTypeDTO[]>> => {
  return authFetch(
    urlWithParams(`/Consumer/FinancialPlanWhatIfs/${whatIfAreaId}WhatIfs`, {
      clientId,
      fpId,
      goalId,
    }),
  );
};

interface UseWhatIfGroupsQueryParams {
  clientId: number;
  fpId: string;
  whatIfAreaId: string;
  goalId?: string;
}

export const useWhatIfGroupsQuery = ({
  clientId,
  fpId,
  whatIfAreaId,
  goalId,
}: UseWhatIfGroupsQueryParams) => {
  return useQuery(
    whatIfsQueryKeys.groups(clientId, fpId, whatIfAreaId, goalId),
    () => fetchWhatIfGroups({ clientId, fpId, whatIfAreaId, goalId }),
    { cacheTime: 0 },
  );
};
